export type LcsRun = {
	start1: number;
	end1: number;
	start2: number;
	end2: number;
};

/** "U" (came from the row above), "L" (came from the column to the left), or the starting row of a
 *  qualifying run that was used to reach this cell. */
type Choice = "U" | "L" | number;

export function lcsDiff(seq1: string, seq2: string, minBoth = 0): LcsRun[] {
	const lower1 = seq1.toLowerCase();
	const lower2 = seq2.toLowerCase();
	const cm = lower1.length;
	const cn = lower2.length;
	const minRun = minBoth + 1;

	const dp: number[][] = Array.from({ length: cm + 1 }, () => new Array<number>(cn + 1).fill(0));
	const choice: (Choice | null)[][] = Array.from({ length: cm + 1 }, () => new Array<Choice | null>(cn + 1).fill(null));

	const diagRunStart = new Map<number, number | null>();
	const diagBestVal = new Map<number, number | null>();
	const diagBestRow = new Map<number, number>();

	for (let i = 1; i <= cm; i++) {
		const ci = lower1[i - 1];
		const row = dp[i];
		const prevRow = dp[i - 1];
		const crow = choice[i];

		for (let j = 1; j <= cn; j++) {
			const d = i - j;

			if (ci !== lower2[j - 1]) {
				diagRunStart.set(d, null);
				diagBestVal.set(d, null);
			} else {
				if (diagRunStart.get(d) == null) {
					diagRunStart.set(d, i);
					diagBestVal.set(d, null);
				}
				const runStart = diagRunStart.get(d) as number;
				const candRow = i - minRun;
				if (candRow >= runStart - 1) {
					const val = dp[candRow][candRow - d] - candRow;
					const bestVal = diagBestVal.get(d);
					if (bestVal == null || val > bestVal) {
						diagBestVal.set(d, val);
						diagBestRow.set(d, candRow);
					}
				}
			}

			const up = prevRow[j];
			const left = row[j - 1];
			let bestVal = up >= left ? up : left;
			let bestChoice: Choice = up >= left ? "U" : "L";

			const bestDiag = diagBestVal.get(d);
			if (bestDiag != null && i + bestDiag > bestVal) {
				bestVal = i + bestDiag;
				bestChoice = diagBestRow.get(d) as number;
			}

			row[j] = bestVal;
			crow[j] = bestChoice;
		}
	}

	const runs: LcsRun[] = [];
	let i = cm;
	let j = cn;
	while (i > 0 && j > 0) {
		const c = choice[i][j];
		if (c === "U") {
			i -= 1;
		} else if (c === "L") {
			j -= 1;
		} else {
			const backRow = c as number;
			const d = i - j;
			const backCol = backRow - d;
			runs.push({ start1: backRow, end1: i, start2: backCol, end2: j });
			i = backRow;
			j = backCol;
		}
	}
	runs.reverse();

	return runs;
}
